import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  Container,
} from "@mui/material";
import { useParams } from "react-router-dom";
import Colors from "assets/Style/Colors";
import { ErrorToaster } from "components/Toaster";
import { PrimaryButton } from "components/Buttons";
import { CommaSeparator, handleExportWithComponent } from "utils";
import LogoImage from "assets/Icons/worldWide_landscape.svg";
import {
  FontFamily,
  InvoiceGlobal,
  InvoiceLocation,
  InvoiceMail,
  InvoicePhone,
} from "assets";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import CurrencyServices from "services/Currency";
import { QRCodeCanvas } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import { PDFExport } from "@progress/kendo-react-pdf";
import { useAuth } from "context/UseContext";
import jsPDF from "jspdf";
import "jspdf/dist/polyfills.es.js";
import html2canvas from "html2canvas";
import { Print } from "@mui/icons-material";
import ShippingServices from "services/Shipping";
const useStyle = makeStyles({
  headingBg: {
    margin: "32px 0px",
    padding: "12px 0px",
    textAlign: "center",
  },
  heading: {
    color: Colors.white,
    textTransform: "uppercase",
    fontWeight: 300,
    fontFamily: FontFamily.openSans,
  },
  text: {
    color: Colors.smokeyGrey,
    fontFamily: FontFamily.openSans,
  },
  tableCell: {
    backgroundColor: Colors.aliceBlue,
    border: "0.25px solid #D9D9D9",
    "& .MuiTypography-root": {
      padding: "4px 12px",
    },
  },
});

function ConsignmentContainerInvoice() {
  const { id } = useParams();
  const contentRef = useRef(null);
  const classes = useStyle();
  const { user } = useAuth();
  const [layoutSelection, setLayoutSelection] = useState({
    text: "A4",
    value: "size-a4",
  });
  const [isContentReady, setIsContentReady] = useState(false);
  const [loader, setLoader] = useState(true);

  // *For Invoice Detail
  const [contanerDetail, setContainerDetail] = useState();

  // *For Currencies
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState();

  // *For Get Invoice Detail
  const getContainerDetail = async () => {
    setLoader(true);
    try {
      let params = {
        container_id: id,
      };
      const { data } = await ShippingServices.getContainerDetails(params);
      setContainerDetail(data);
      getCurrencies(data?.invoice?.booking?.currency);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  // *For Get Currencies
  const getCurrencies = async (currency) => {
    try {
      let params = {
        detailed: true,
      };
      const { data } = await CurrencyServices.getCurrencies(params);
      const rate = data?.currencies.find(
        (e) => e.currency === currency
      )?.conversion_rate;
      setCurrencyExchangeRate(rate);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  useEffect(() => {
    if (id) {
      getContainerDetail();
    }
  }, [id]);

  useEffect(() => {
    if (contentRef.current) {
      // console.log("Content ref is set:", contentRef.current);
      setIsContentReady(true);
    } else {
      console.warn(
        "contentRef.current is null or undefined during component mount"
      );
    }
  }, [contentRef]);

  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
    documentTitle: "Invoice",
    onBeforePrint: () => {
      const content = contentRef.current;
      if (content && isContentReady) {
        content.style.transform = "scale(0.83)";
        content.style.transformOrigin = "top center";
      } else {
        console.warn("contentRef.current is not available in onBeforePrint");
      }
    },
    onAfterPrint: () => {
      try {
        const content = contentRef.current;
        if (content) {
          content.style.transform = "none";
        } else {
          console.warn(
            "contentRef.current is null or undefined in onAfterPrint"
          );
        }
      } catch (error) {
        console.error("Error in onAfterPrint:", error);
      }
    },
  });

  const openInvoice = () => {
    const content = contentRef.current;

    if (!content) {
      console.error("Content is not attached to the DOM yet");
      return;
    }

    // console.log("Content is attached:", content);

    setTimeout(() => {
      if (!contentRef.current) {
        console.error(
          "Content was removed before html2canvas could be executed"
        );
        return;
      }

      html2canvas(content)
        .then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "mm", "a4");
          const imgWidth = 210; // A4 width in mm
          const pageHeight = 297; // A4 height in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let position = 0;

          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

          // Save the PDF file locally
          pdf.save("invoice.pdf");

          // Open the PDF in a new tab
          const pdfData = pdf.output("dataurlstring");
          window.open(pdfData); // Attempt to open in a new tab
        })
        .catch((error) => {
          console.error("Error creating canvas or PDF:", error);
        });
    }, 500); // Adjust the delay as needed
  };
  const handleExportWithComponentNew = (pdfExportComponent) => {
    if (pdfExportComponent.current) {
      pdfExportComponent.current.save(); // Save the PDF directly, but no `then()` chaining here

      // Capture the content and convert it to a Blob to open in a new tab
      const element = pdfExportComponent.current.contentElement;
      const pdfExport = new PDFExport({
        paperSize: "A4",
        forcePageBreak: ".page-break",
        margin: 40,
      });

      pdfExport.save(element).then((dataUri) => {
        const blob = new Blob([dataUri], { type: "application/pdf" });
        const pdfURL = URL.createObjectURL(blob);
        window.open(pdfURL, "_blank", 'noopener,noreferrer'); // Open in new tab
      });
    }
  };

  let newVehicle = contanerDetail?.invoice?.booking?.new_vehicle;
  const allHaveShippingCharges = contanerDetail?.shipping?.vehicles?.every(
    (val) => parseFloat(val?.client_costing?.shipping_charges || 0) > 0
  );

  const anyHasShippingCharges = contanerDetail?.shipping?.vehicles?.some(
    (val) => parseFloat(val?.client_costing?.shipping_charges || 0) > 0
  );

  const vehicles = contanerDetail?.shipping?.vehicles || [];

  // SUM of all total_v2 (shipping total)
  const totalAmount = vehicles.reduce(
    (sum, v) => sum + (parseFloat(v?.client_costing?.total_v2) || 0),
    0
  );

  // SUM of all invoice paid amounts
  const paidAmount = vehicles.reduce(
    (sum, v) => sum + (parseFloat(v?.invoice?.paid) || 0),
    0
  );

  // SUM of all invoice balance amounts
  const balanceAmount = vehicles.reduce(
    (sum, v) => sum + (parseFloat(v?.invoice?.balance) || 0),
    0
  );

  const result_vehicle = contanerDetail?.shipping?.vehicles || [];

  // Step 1: Find the vehicle with the earliest arrived_port_date

  // Step 1: Get earliest vehicle by client_costing.createdAt
  const earliestVehicle = result_vehicle
    .filter((v) => v?.client_costing?.createdAt) // ensure valid dates
    .sort(
      (a, b) =>
        new Date(a.client_costing.createdAt) -
        new Date(b.client_costing.createdAt)
    )[0];

  // Calculate Past Due Days using moment
  let pastDueDays = 0;
  if (earliestVehicle?.arrived_port_date) {
    const arrived = moment(earliestVehicle.arrived_port_date);
    const today = moment();
    const diff = today.diff(arrived, "days");
    pastDueDays = diff > 0 ? diff : 0; // never negative
  }

  const result = earliestVehicle
    ? {
        created_at: earliestVehicle.client_costing?.createdAt,
        arrived_port_date: earliestVehicle.arrived_port_date,
        past_due_days: pastDueDays,
      }
    : null;

  return (
    <Container>
      {newVehicle === false ? (
        <>
          {!loader && (
            <Box
              sx={{
                textAlign: "right",
                p: 4,
                alignItems: "center",
                justifyContent: "flex-end",
                display: "flex",
              }}
            >
              <PrimaryButton
                title="Download Invoice"
                type="button"
                style={{ backgroundColor: Colors.bluishCyan }}
                onClick={() => handleExportWithComponent(contentRef)}
              />
              {/* <PrimaryButton
            title="Print Invoice"
            type="button"
            style={{ backgroundColor: Colors.bluishCyan, marginLeft:5 }}
            onClick={() => handlePrint()}
          /> */}
              <Box
                component="div"
                sx={{
                  display: "inline-block",
                  marginLeft: 2,
                  cursor: "pointer",
                }}
                onClick={() => handlePrint()}
              >
                <Print
                  sx={{
                    height: 30,
                    width: 30,
                    cursor: "pointer",
                    color: Colors.primary,
                  }}
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              width: "1100px",
              mx: 4,
              my: 2,
              bgcolor: Colors.white,
              boxShadow: "0px 8px 18px 0px #9B9B9B1A",
            }}
          >
            {!loader ? (
              <PDFExport ref={contentRef} fileName="Vehicle Invoice">
                <Box className={`pdf-page ${layoutSelection.value}`}>
                  <Box>
                    <Grid container spacing={0}>
                      <Box
                        sx={{
                          width: { xs: "100%", md: "25%" },
                          "@media print": {
                            width: "25%",
                            flex: "0 0 25%",
                          },
                          marginTop: "5px",
                        }}
                      >
                        <Box
                          component="img"
                          src={LogoImage}
                          alt="Ship Logo"
                          sx={{
                            width: "100%",
                            objectFit: "contain",
                            display: "block",
                            maxHeight: "100px",
                          }}
                        />
                      </Box>

                      {/* Right Contact Info */}
                      <Box
                        sx={{
                          backgroundColor: Colors.green,
                          padding: 2,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          width: { xs: "100%", md: "71.5%" },
                          marginRight: "3.5%",
                          "@media print": {
                            width: "71.5%",
                            flex: "0 0 71.5%",
                          },
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <InvoicePhone />
                              <Typography
                                variant="body1"
                                sx={{
                                  color: Colors.whiteSmoke,
                                  fontFamily: FontFamily.openSans,
                                  fontSize: 14,
                                }}
                              >
                                +971 6 510 2000
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <InvoiceMail />
                              <Typography
                                variant="body1"
                                sx={{
                                  color: Colors.whiteSmoke,
                                  fontFamily: FontFamily.openSans,
                                  fontSize: 14,
                                }}
                              >
                                info@galaxyshipping.com
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <InvoiceGlobal />
                              <Typography
                                variant="body1"
                                sx={{
                                  color: Colors.whiteSmoke,
                                  fontFamily: FontFamily.openSans,
                                  fontSize: 14,
                                }}
                              >
                                https://galaxyshipping.com
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <InvoiceLocation />
                              <Typography
                                variant="body1"
                                sx={{
                                  color: Colors.whiteSmoke,
                                  fontFamily: FontFamily.openSans,
                                  fontSize: 14,
                                }}
                              >
                                Ind Area#4 P.O Box 83126, Sharjah, UAE
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    <Box
                      sx={{
                        my: 5,
                        position: "relative",
                        bgcolor: Colors.primary,
                        width: 1,
                        height: "12px",
                      }}
                    >
                      <Typography
                        component={"span"}
                        variant="h2"
                        className={classes.text}
                        sx={{
                          color: Colors.charcoalGrey,
                          bgcolor: Colors.white,
                          fontFamily:FontFamily.openSans,
                          p: 2,
                          letterSpacing: "3px",
                          position: "absolute",
                          right: "93px",
                          top: "-40px",
                        }}
                      >
                        Container Consignment{" "}
                      </Typography>
                    </Box>

                    {!allHaveShippingCharges && (
                      <Typography
                        className={classes.text}
                        sx={{
                          textAlign: "center",
                          fontFamily: "Helvetica",
                          fontWeight: 700,
                          padding: "10px",
                          fontSize: "16px",
                          color: "#FE2E1B",
                          marginTop: "12px",
                        }}
                      >
                        Consignment Invoice is not yet finalized. Please contact
                        the costing department before sharing.{" "}
                      </Typography>
                    )}
                    <Box sx={{ px: "20px" }}>
                      <Box
                        sx={{
                          mt: 2,
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          overflow: "hidden",
                          width: "100%",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "flex",
                            height: 40,
                            width: "100%",
                          }}
                        >
                          {/* Light Green Triangle that fills the clipped corner */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: "calc(46% - 15%)",
                              width: "15%",
                              height: "100%",
                              backgroundColor: "#6cc04a",
                              zIndex: 0,
                            }}
                          />

                          {/* Left Header with diagonal cut */}
                          <Box
                            sx={{
                              width: "46%",
                              backgroundColor: "#0a5b2d",
                              clipPath:
                                "polygon(0 0, 85% 0, 100% 100%, 0% 100%)", // reverse the clip path (top to bottom)
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "20px",
                              zIndex: 1,
                            }}
                            className={classes.text}
                          >
                            Bill To{" "}
                          </Box>

                          {/* Right Header */}
                          <Box
                            sx={{
                              width: "54%",
                              backgroundColor: "#6cc04a",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "20px",
                              zIndex: 1,
                            }}
                            className={classes.text}
                          >
                            Invoice Detail
                          </Box>
                        </Box>

                        <Grid container>
                          <Grid item xs={5.5} sx={{ px: 2, py: 1.5 }}>
                            <Grid container sx={{ padding: "10px" }}>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontFamily: FontFamily.NunitoRegular }}
                                >
                                  {" "}
                                  Customer ID:{" "}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontFamily: FontFamily.NunitoRegular,
                                    textAlign: "right",
                                  }}
                                >
                                  {contanerDetail?.shipping?.vehicles?.[0]
                                    ?.booking?.customer?.id ?? "-"}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontFamily: FontFamily.NunitoRegular }}
                                >
                                  Customer Name:
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontFamily: FontFamily.NunitoRegular,
                                    textAlign: "right",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {contanerDetail?.shipping?.vehicles?.[0]
                                    ?.booking?.customer?.name ?? "-"}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontFamily: FontFamily.NunitoRegular }}
                                >
                                  Email ID:
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontFamily: FontFamily.NunitoRegular,
                                    textAlign: "right",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {contanerDetail?.shipping?.vehicles?.[0]
                                    ?.booking?.customer?.email ?? "-"}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontFamily: FontFamily.NunitoRegular }}
                                >
                                  Whatsapp No:
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontFamily: FontFamily.NunitoRegular,
                                    textAlign: "right",
                                  }}
                                >
                                  {contanerDetail?.shipping?.vehicles?.[0]
                                    ?.booking?.customer?.uae_phone ?? "-"}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Box
                            sx={{
                              width: "1px",
                              backgroundColor: "#ccc",
                              minHeight: "45px",
                            }}
                          />
                          <Grid item xs={6.4} sx={{ px: 2, py: 1.5 }}>
                            <Grid container spacing={0.5} sx={{ px: 1 }}>
                              <Grid item xs={6}>
                                <Grid container>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      Invoice no:
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      sx={{ fontSize: "16px" }}
                                      variant="body1"
                                    >
                                      {`GCC-${id}`}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      Invoice on:{" "}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      {moment(result?.created_at).format(
                                        "DD/MM/YYYY"
                                      )}{" "}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      Creation on:{" "}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      {moment(result?.created_at).format(
                                        "DD/MM/YYYY"
                                      )}{" "}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      Due on:{" "}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      {moment(result?.arrived_port_date).format(
                                        "DD/MM/YYYY"
                                      )}{" "}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={6}>
                                <Grid container>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      Past Due Days:
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      {result?.past_due_days}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                      }}
                                    >
                                      Balance Due:
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography
                                      sx={{ fontSize: "16px" }}
                                      variant="body1"
                                    >
                                      ${CommaSeparator(balanceAmount)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>

                      <Grid container sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                          <Typography
                            className={classes.text}
                            variant="h6"
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              padding: "8px",
                              background:
                                "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                              color: Colors.whiteSmoke,
                              fontWeight: "bold",
                            }}
                          >
                            Cargo Information{" "}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sx={{ px: 1, py: 1.5, border: "1px solid #ccc" }}
                        >
                          <Grid
                            container
                            sx={{ padding: "10px" }}
                            spacing={0.5}
                          >
                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Invoice no:
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">GVI-{id}</Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Shipper:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">Galaxy</Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Destination:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">
                                {contanerDetail?.shipping?.dest?.name}
                              </Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Shipping line:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">
                                {contanerDetail?.shipping?.ship_line?.name}
                              </Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Booking no:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">
                                {contanerDetail?.shipping?.booking_no}
                              </Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Loading Date:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">
                                {contanerDetail?.shipping?.vehicles?.[0]
                                  ?.loading_date
                                  ? moment(
                                      contanerDetail?.shipping?.vehicles?.[0]
                                        ?.loading_date
                                    ).format("DD/MM/YYYY")
                                  : "-"}
                              </Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Consignee:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">
                                Galaxy Used Cars Tr LLC{" "}
                              </Typography>
                            </Grid>

                            <Grid item xs={3}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                ETA:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">
                                {contanerDetail?.shipping?.vehicles?.[0]?.eta
                                  ? moment(
                                      contanerDetail?.shipping?.vehicles?.[0]
                                        ?.eta
                                    ).format("DD/MM/YYYY")
                                  : "-"}{" "}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Box
                        sx={{
                          mt: 2,
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          overflow: "hidden",
                          width: "100%",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "flex",
                            height: 40,
                            width: "100%",
                          }}
                        >
                          {/* Light Green Triangle for the diagonal blend */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: "50%", // <-- FIXED HERE (perfect alignment)
                              width: "15%",
                              height: "100%",
                              backgroundColor: "#6cc04a",
                              zIndex: 0,
                            }}
                          />

                          {/* SR NO COLUMN (10%) */}
                          <Box
                            sx={{
                              width: "10%",
                              backgroundColor: "#0a5b2d",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "20px",
                              zIndex: 1,
                            }}
                          >
                            Sr. No
                          </Box>

                          {/* DESCRIPTION COLUMN (55%) */}
                          <Box
                            className={classes.text}
                            sx={{
                              width: "55%",
                              backgroundColor: "#0a5b2d",
                              clipPath:
                                "polygon(0 0, 85% 0, 100% 100%, 0% 100%)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "20px",
                              zIndex: 1,
                            }}
                          >
                            Description
                          </Box>

                          {/* SHIPPING CHARGES COLUMN (35%) */}
                          <Box
                            className={classes.text}
                            sx={{
                              width: "35%",
                              backgroundColor: "#6cc04a",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "20px",
                              zIndex: 1,
                            }}
                          >
                            Shipping Charges
                          </Box>
                        </Box>

                        {/* Body */}
                        <Grid container>
                          {/* -------- VEHICLE ROWS (ALIGNED) -------- */}
                          {contanerDetail?.shipping?.vehicles?.map(
                            (val, idx) => (
                              <Grid container key={idx}>
                                <Grid
                                  item
                                  xs={1.2} // 10%
                                  sx={{
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "45px",
                                    fontWeight: "bold",
                                    fontFamily: FontFamily.NunitoRegular,
                                  }}
                                >
                                  {idx + 1}
                                </Grid>
                                <Box
                                  sx={{
                                    width: "1px",
                                    backgroundColor: "#ccc",
                                    minHeight: "45px",
                                  }}
                                />
                                {/* LEFT CELL */}
                                <Grid
                                  item
                                  xs={6.59}
                                  sx={{
                                    py: 1,
                                    px: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    minHeight: "45px", // <-- Same height both sides
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    {`${val?.booking?.buyer?.name}  ${val?.booking?.buyer?.auction?.name} - 
          ${val?.booking?.veh_model?.name} ${val?.booking?.veh_make?.name} 
          ${val?.booking?.color} #${val?.booking?.vin}`}
                                  </Typography>
                                </Grid>

                                {/* DIVIDER */}
                                <Box
                                  sx={{
                                    width: "1px",
                                    backgroundColor: "#ccc",
                                    minHeight: "45px",
                                  }}
                                />

                                {/* RIGHT CELL */}
                                <Grid
                                  item
                                  xs={4}
                                  sx={{
                                    py: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: "45px", // <-- Exact height match
                                  }}
                                >
                                  {parseFloat(
                                    val?.client_costing?.shipping_charges || 0
                                  ) > 0 ? (
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: FontFamily.NunitoRegular,
                                        textAlign: "center",
                                      }}
                                    >
                                      $
                                      {CommaSeparator(
                                        val?.client_costing?.total_v2
                                      )}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      className={classes.text}
                                      sx={{
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "red",
                                        opacity: 0.5,
                                        textAlign: "center",
                                      }}
                                    >
                                      NOT PAID
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            )
                          )}

                          {/* -------- TOTAL / PAID / BALANCE ROWS -------- */}
                          {anyHasShippingCharges && (
                            <>
                              {/* TOTAL */}
                              <Grid container>
                                <Grid
                                  item
                                  xs={7.8}
                                  className={classes.text}
                                  sx={{
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    py: 0.5,
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  Total
                                </Grid>

                                <Box
                                  sx={{
                                    width: "1px",
                                    backgroundColor: "#ccc",
                                    minHeight: "40px",
                                  }}
                                />

                                <Grid
                                  item
                                  xs={4}
                                  className={classes.text}
                                  sx={{
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    py: 0.5,
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  ${CommaSeparator(totalAmount)}
                                </Grid>
                              </Grid>

                              {/* PAID */}
                              <Grid container>
                                <Grid
                                  item
                                  xs={7.8}
                                  className={classes.text}
                                  sx={{
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    py: 0.5,
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  Paid
                                </Grid>

                                <Box
                                  sx={{
                                    width: "1px",
                                    backgroundColor: "#ccc",
                                    minHeight: "40px",
                                  }}
                                />

                                <Grid
                                  item
                                  xs={4}
                                  className={classes.text}
                                  sx={{
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    py: 0.5,
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  ${CommaSeparator(paidAmount)}
                                </Grid>
                              </Grid>

                              {/* BALANCE DUE */}
                              <Grid container>
                                <Grid
                                  item
                                  xs={7.8}
                                  className={classes.text}
                                  sx={{
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    py: 0.5,
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  Balance Due
                                </Grid>

                                <Box
                                  sx={{
                                    width: "1px",
                                    backgroundColor: "#ccc",
                                    minHeight: "40px",
                                  }}
                                />

                                <Grid
                                  item
                                  xs={4}
                                  className={classes.text}
                                  sx={{
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    py: 0.5,
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  ${CommaSeparator(balanceAmount)}
                                </Grid>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>

                      <Grid
                        container
                        spacing={2}
                        justifyContent="space-between"
                      >
                        <Grid item xs={12} sm={6}>
                          <Grid
                            container
                            spacing={0.5}
                            sx={{ border: "1px solid #ddd", marginTop: "10px" }}
                          >
                            <Grid item xs={12}>
                              <Typography
                                className={classes.text}
                                variant="h6"
                                sx={{
                                  fontFamily: "Helvetica",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  padding: "8px",
                                  background: "#FFFCFC",
                                }}
                              >
                                Payment History
                              </Typography>
                            </Grid>
                            <Grid md={12} sx={{ paddingLeft: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: "100%",
                                }}
                              >
                                <Box
                                  sx={{
                                    overflowX: "auto",
                                    maxWidth: "100%",
                                    paddingLeft: 0,
                                    marginLeft: 0,
                                  }}
                                >
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                      margin: 0,
                                      padding: 0,
                                      tableLayout: "fixed",
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        className={classes.text}
                                        style={{
                                          background: Colors.green,
                                          color: Colors.whiteSmoke,
                                          fontWeight: "bold",
                                        }}
                                      >
                                        <th
                                          className={classes.text}
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #ddd",
                                            fontSize: "12px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Pmt Type
                                        </th>
                                        <th
                                          className={classes.text}
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #ddd",
                                            fontSize: "12px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Paid on
                                        </th>

                                        <th
                                          className={classes.text}
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #ddd",
                                            fontSize: "12px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Mode
                                        </th>
                                        <th
                                          className={classes.text}
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #ddd",
                                            fontSize: "12px",
                                            textAlign: "center",
                                          }}
                                        >
                                          USD
                                        </th>
                                        <th
                                          className={classes.text}
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #ddd",
                                            whiteSpace: "pre-line",
                                            fontSize: "12px",
                                            textAlign: "center",
                                          }}
                                        >
                                          AED
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {contanerDetail?.shipping?.vehicles?.map(
                                        (val, idx) =>
                                          val?.invoice?.payment?.map(
                                            (pay, pIdx) => (
                                              <>
                                                <tr
                                                  className={classes.text}
                                                  style={{
                                                    background: "#FFFCFC",
                                                  }}
                                                  key={`${idx}-${pIdx}`}
                                                >
                                                  <td
                                                    className={classes.text}
                                                    style={{
                                                      padding: "8px",
                                                      borderBottom:
                                                        "1px solid #ddd",
                                                      borderRight:
                                                        "1px solid #ddd",
                                                      fontSize: "12px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {pay?.shipping
                                                      ? "Shipping"
                                                      : "Vehicle"}
                                                  </td>
                                                  <td
                                                    className={classes.text}
                                                    style={{
                                                      padding: "8px",
                                                      borderBottom:
                                                        "1px solid #ddd",
                                                      borderRight:
                                                        "1px solid #ddd",
                                                      fontSize: "12px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {moment(
                                                      pay?.payment_date
                                                    ).format("DD-MM-YYYY")}
                                                  </td>
                                                  <td
                                                    className={classes.text}
                                                    style={{
                                                      padding: "8px",
                                                      borderBottom:
                                                        "1px solid #ddd",
                                                      borderRight:
                                                        "1px solid #ddd",
                                                      fontSize: "12px",
                                                      textAlign: "center",
                                                      textTransform:
                                                        "capitalize",
                                                    }}
                                                  >
                                                    {pay?.payment_medium || "-"}
                                                  </td>
                                                  <td
                                                    className={classes.text}
                                                    style={{
                                                      padding: "8px",
                                                      borderBottom:
                                                        "1px solid #ddd",
                                                      borderRight:
                                                        "1px solid #ddd",
                                                      fontSize: "12px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    $
                                                    {CommaSeparator(
                                                      (pay?.currency?.toLowerCase() !==
                                                      "aed"
                                                        ? (Number(
                                                            pay?.shipping_amount
                                                          ) || 0) /
                                                          (Number(pay?.rate) ||
                                                            1)
                                                        : Number(
                                                            pay?.shipping_amount
                                                          ) || 0
                                                      ).toFixed(2)
                                                    )}
                                                  </td>
                                                  <td
                                                    className={classes.text}
                                                    style={{
                                                      padding: "8px",
                                                      borderBottom:
                                                        "1px solid #ddd",
                                                      borderRight:
                                                        "1px solid #ddd",
                                                      fontSize: "12px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {CommaSeparator(
                                                      (pay?.currency?.toLowerCase() !==
                                                      "aed"
                                                        ? Number(
                                                            pay?.shipping_amount
                                                          ) || 0
                                                        : (Number(
                                                            pay?.shipping_amount
                                                          ) || 0) *
                                                          (Number(pay?.rate) ||
                                                            0)
                                                      ).toFixed(2)
                                                    )}
                                                  </td>
                                                </tr>
                                              </>
                                            )
                                          )
                                      )}
                                    </tbody>
                                  </table>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            textAlign: "end",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "end",
                          }}
                        >
                          <QRCodeCanvas
                            value={
                              window.location.origin +
                              `/consignment-container/${btoa(id)}`
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box
                      sx={{
                        pl: 4,
                        pr: 3,
                        py: 1,
                        bgcolor: Colors.primary,
                        mt: 4,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: Colors.white,
                          fontFamily: FontFamily.openSans,
                        }}
                      >
                        Customer care Contact: Mohammed husni - +971523195682
                        (Arabic & English ) Ardamehr Shoev - +971545836028
                        (English ,Arabic, Tajik & Farsi)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: Colors.white,
                          fontFamily: FontFamily.openSans,
                        }}
                      >
                        Ravin abdul kareem - +971528293801 (Kurdish , Arabic &
                        English) Magsat Gylyjov - +97158666403 (Turken , Russian
                        & English)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: Colors.white,
                          fontFamily: FontFamily.openSans,
                        }}
                      >
                        Ind Area#4 P.O Box 83126, Sharjah, UAE.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PDFExport>
            ) : (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          {!loader && (
            <Box
              sx={{
                textAlign: "right",
                p: 4,
                alignItems: "center",
                justifyContent: "flex-end",
                display: "flex",
              }}
            >
              <PrimaryButton
                title="Download Invoice"
                type="button"
                style={{ backgroundColor: Colors.bluishCyan }}
                onClick={() => handleExportWithComponent(contentRef)}
              />
              <Box
                component="div"
                sx={{
                  display: "inline-block",
                  marginLeft: 2,
                  cursor: "pointer",
                }}
                onClick={() => handlePrint()}
              >
                <Print
                  sx={{
                    height: 30,
                    width: 30,
                    cursor: "pointer",
                    color: Colors.primary,
                  }}
                />
              </Box>
            </Box>
          )}
          <PDFExport ref={contentRef} fileName="Vehicle Invoice">
            <Box
              sx={{
                width: "1000px",
                mx: 4,
                my: 2,
                bgcolor: Colors.white,
                boxShadow: "0px 8px 18px 0px #9B9B9B1A",
              }}
              className={`pdf-page ${layoutSelection.value}`}
            >
              {!loader ? (
                <Box>
                  <Grid container spacing={0}>
                    <Box
                      sx={{
                        width: { xs: "100%", md: "25%" },
                        "@media print": {
                          width: "25%",
                          flex: "0 0 25%",
                        },
                        marginTop: "5px",
                      }}
                    >
                      <Box
                        component="img"
                        src={LogoImage}
                        alt="Ship Logo"
                        sx={{
                          width: "100%",
                          objectFit: "contain",
                          display: "block",
                          maxHeight: "100px",
                        }}
                      />
                    </Box>

                    {/* Right Contact Info */}
                    <Box
                      sx={{
                        backgroundColor: Colors.green,
                        padding: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        width: { xs: "100%", md: "71.5%" },
                        marginRight: "3.5%",
                        "@media print": {
                          width: "71.5%",
                          flex: "0 0 71.5%",
                        },
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <InvoicePhone />
                            <Typography
                              variant="body1"
                              sx={{
                                color: Colors.whiteSmoke,
                                fontFamily: FontFamily.openSans,
                                fontSize: 14,
                              }}
                            >
                              +971 6 510 2000
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <InvoiceMail />
                            <Typography
                              variant="body1"
                              sx={{
                                color: Colors.whiteSmoke,
                                fontFamily: FontFamily.openSans,
                                fontSize: 14,
                              }}
                            >
                              info@galaxyshipping.com
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <InvoiceGlobal />
                            <Typography
                              variant="body1"
                              sx={{
                                color: Colors.whiteSmoke,
                                fontFamily: FontFamily.openSans,
                                fontSize: 14,
                              }}
                            >
                              https://galaxyshipping.com
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <InvoiceLocation />
                            <Typography
                              variant="body1"
                              sx={{
                                color: Colors.whiteSmoke,
                                fontFamily: FontFamily.openSans,
                                fontSize: 14,
                              }}
                            >
                              Ind Area#4 P.O Box 83126, Sharjah, UAE
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  <Box
                    sx={{
                      my: 5,
                      position: "relative",
                      bgcolor: Colors.primary,
                      width: 1,
                      height: "12px",
                    }}
                  >
                    <Typography
                      component={"span"}
                      variant="h2"
                      sx={{
                        color: Colors.charcoalGrey,
                        bgcolor: Colors.white,
                        p: 2,
                        letterSpacing: "3px",
                        position: "absolute",
                        right: "90px",
                        top: "-40px",
                      }}
                    >
                      Container Consignment{" "}
                    </Typography>
                  </Box>

                  {!allHaveShippingCharges && (
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontFamily: "Helvetica",
                        fontWeight: 700,
                        padding: "10px",
                        fontSize: "16px",
                        color: "#FE2E1B",
                        marginTop: "12px",
                      }}
                    >
                      Consignment Invoice is not yet finalized. Please contact
                      the costing department before sharing.{" "}
                    </Typography>
                  )}
                  <Box sx={{ px: "20px" }}>
                    <Box
                      sx={{
                        mt: 2,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        overflow: "hidden",
                        width: "100%",
                        fontSize: "13px",
                        marginBottom: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          height: 40,
                          width: "100%",
                        }}
                      >
                        {/* Light Green Triangle that fills the clipped corner */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: "calc(46% - 15%)",
                            width: "15%",
                            height: "100%",
                            backgroundColor: "#6cc04a",
                            zIndex: 0,
                          }}
                        />

                        {/* Left Header with diagonal cut */}
                        <Box
                          sx={{
                            width: "46%",
                            backgroundColor: "#0a5b2d",
                            clipPath: "polygon(0 0, 85% 0, 100% 100%, 0% 100%)", // reverse the clip path (top to bottom)
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "20px",
                            zIndex: 1,
                          }}
                        >
                          Bill To{" "}
                        </Box>

                        {/* Right Header */}
                        <Box
                          sx={{
                            width: "54%",
                            backgroundColor: "#6cc04a",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "20px",
                            zIndex: 1,
                          }}
                        >
                          Invoice Detail
                        </Box>
                      </Box>

                      <Grid container>
                        <Grid item xs={5.5} sx={{ px: 2, py: 1.5 }}>
                          <Grid container sx={{ padding: "10px" }}>
                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                {" "}
                                Customer ID:{" "}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontFamily: FontFamily.NunitoRegular,
                                  textAlign: "right",
                                }}
                              >
                                {contanerDetail?.shipping?.vehicles?.[0]
                                  ?.booking?.customer?.id ?? "-"}
                              </Typography>
                            </Grid>

                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Customer Name:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontFamily: FontFamily.NunitoRegular,
                                  textAlign: "right",
                                  wordBreak: "break-all",
                                }}
                              >
                                {contanerDetail?.shipping?.vehicles?.[0]
                                  ?.booking?.customer?.name ?? "-"}
                              </Typography>
                            </Grid>

                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Email ID:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontFamily: FontFamily.NunitoRegular,
                                  textAlign: "right",
                                  wordBreak: "break-all",
                                }}
                              >
                                {contanerDetail?.shipping?.vehicles?.[0]
                                  ?.booking?.customer?.email ?? "-"}
                              </Typography>
                            </Grid>

                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                Whatsapp No:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontFamily: FontFamily.NunitoRegular,
                                  textAlign: "right",
                                }}
                              >
                                {contanerDetail?.shipping?.vehicles?.[0]
                                  ?.booking?.customer?.uae_phone ?? "-"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Box
                          sx={{
                            width: "1px",
                            backgroundColor: "#ccc",
                            minHeight: "45px",
                          }}
                        />
                        <Grid item xs={6.4} sx={{ px: 2, py: 1.5 }}>
                          <Grid container spacing={0.5} sx={{ px: 1 }}>
                            <Grid item xs={6}>
                              <Grid container>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    Invoice no:
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    sx={{ fontSize: "16px" }}
                                    variant="body1"
                                  >
                                    {`GCC-${id}`}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    Invoice on:{" "}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    {moment(result?.created_at).format(
                                      "DD/MM/YYYY"
                                    )}{" "}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    Creation on:{" "}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    {moment(result?.created_at).format(
                                      "DD/MM/YYYY"
                                    )}{" "}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    Due on:{" "}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    {moment(result?.arrived_port_date).format(
                                      "DD/MM/YYYY"
                                    )}{" "}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={6}>
                              <Grid container>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    Past Due Days:
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    {result?.past_due_days}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "16px",
                                      fontFamily: FontFamily.NunitoRegular,
                                    }}
                                  >
                                    Balance Due:
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography
                                    sx={{ fontSize: "16px" }}
                                    variant="body1"
                                  >
                                    ${CommaSeparator(balanceAmount)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>

                    <Grid container sx={{ mt: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Helvetica",
                            textAlign: "center",
                            fontWeight: "bold",
                            padding: "8px",
                            background:
                              "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
                            color: Colors.whiteSmoke,
                            fontWeight: "bold",
                          }}
                        >
                          Cargo Information{" "}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sx={{ px: 1, py: 1.5, border: "1px solid #ccc" }}
                      >
                        <Grid container sx={{ padding: "10px" }} spacing={0.5}>
                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Invoice no:
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">GVI-{id}</Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Shipper:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">Galaxy</Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Destination:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">
                              {contanerDetail?.shipping?.dest?.name}
                            </Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Shipping line:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">
                              {contanerDetail?.shipping?.ship_line?.name}
                            </Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Booking no:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">
                              {contanerDetail?.shipping?.booking_no}
                            </Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Loading Date:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">
                              {contanerDetail?.shipping?.vehicles?.[0]
                                ?.loading_date
                                ? moment(
                                    contanerDetail?.shipping?.vehicles?.[0]
                                      ?.loading_date
                                  ).format("DD/MM/YYYY")
                                : "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              Consignee:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">
                              Galaxy Used Cars Tr LLC{" "}
                            </Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: FontFamily.NunitoRegular }}
                            >
                              ETA:{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body1">
                              {contanerDetail?.shipping?.vehicles?.[0]?.eta
                                ? moment(
                                    contanerDetail?.shipping?.vehicles?.[0]?.eta
                                  ).format("DD/MM/YYYY")
                                : "-"}{" "}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        mt: 2,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        overflow: "hidden",
                        width: "100%",
                        fontSize: "13px",
                        marginBottom: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          height: 40,
                          width: "100%",
                        }}
                      >
                        {/* Light Green Triangle for the diagonal blend */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: "50%", // <-- FIXED HERE (perfect alignment)
                            width: "15%",
                            height: "100%",
                            backgroundColor: "#6cc04a",
                            zIndex: 0,
                          }}
                        />

                        {/* SR NO COLUMN (10%) */}
                        <Box
                          sx={{
                            width: "10%",
                            backgroundColor: "#0a5b2d",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "20px",
                            zIndex: 1,
                          }}
                        >
                          Sr. No
                        </Box>

                        {/* DESCRIPTION COLUMN (55%) */}
                        <Box
                          sx={{
                            width: "55%",
                            backgroundColor: "#0a5b2d",
                            clipPath: "polygon(0 0, 85% 0, 100% 100%, 0% 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "20px",
                            zIndex: 1,
                          }}
                        >
                          Description
                        </Box>

                        {/* SHIPPING CHARGES COLUMN (35%) */}
                        <Box
                          sx={{
                            width: "35%",
                            backgroundColor: "#6cc04a",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "20px",
                            zIndex: 1,
                          }}
                        >
                          Shipping Charges
                        </Box>
                      </Box>

                      {/* Body */}
                      <Grid container>
                        {/* -------- VEHICLE ROWS (ALIGNED) -------- */}
                        {contanerDetail?.shipping?.vehicles?.map((val, idx) => (
                          <Grid container key={idx}>
                            <Grid
                              item
                              xs={1.2} // 10%
                              sx={{
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "45px",
                                fontWeight: "bold",
                                fontFamily: FontFamily.NunitoRegular,
                              }}
                            >
                              {idx + 1}
                            </Grid>
                            <Box
                              sx={{
                                width: "1px",
                                backgroundColor: "#ccc",
                                minHeight: "45px",
                              }}
                            />
                            {/* LEFT CELL */}
                            <Grid
                              item
                              xs={6.59}
                              sx={{
                                py: 1,
                                px: 2,
                                display: "flex",
                                alignItems: "center",
                                minHeight: "45px", // <-- Same height both sides
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontFamily: FontFamily.NunitoRegular }}
                              >
                                {`${val?.booking?.buyer?.name}  ${val?.booking?.buyer?.auction?.name} - 
          ${val?.booking?.veh_model?.name} ${val?.booking?.veh_make?.name} 
          ${val?.booking?.color} #${val?.booking?.vin}`}
                              </Typography>
                            </Grid>

                            {/* DIVIDER */}
                            <Box
                              sx={{
                                width: "1px",
                                backgroundColor: "#ccc",
                                minHeight: "45px",
                              }}
                            />

                            {/* RIGHT CELL */}
                            <Grid
                              item
                              xs={4}
                              sx={{
                                py: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "45px", // <-- Exact height match
                              }}
                            >
                              {parseFloat(
                                val?.client_costing?.shipping_charges || 0
                              ) > 0 ? (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "16px",
                                    fontFamily: FontFamily.NunitoRegular,
                                    textAlign: "center",
                                  }}
                                >
                                  $
                                  {CommaSeparator(
                                    val?.client_costing?.total_v2
                                  )}
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "red",
                                    opacity: 0.5,
                                    textAlign: "center",
                                  }}
                                >
                                  NOT PAID
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        ))}

                        {/* -------- TOTAL / PAID / BALANCE ROWS -------- */}
                        {anyHasShippingCharges && (
                          <>
                            {/* TOTAL */}
                            <Grid container>
                              <Grid
                                item
                                xs={7.8}
                                sx={{
                                  borderTop: "1px solid #ccc",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  py: 0.5,
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                Total
                              </Grid>

                              <Box
                                sx={{
                                  width: "1px",
                                  backgroundColor: "#ccc",
                                  minHeight: "40px",
                                }}
                              />

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  borderTop: "1px solid #ccc",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  py: 0.5,
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                ${CommaSeparator(totalAmount)}
                              </Grid>
                            </Grid>

                            {/* PAID */}
                            <Grid container>
                              <Grid
                                item
                                xs={7.8}
                                sx={{
                                  borderTop: "1px solid #ccc",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  py: 0.5,
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                Paid
                              </Grid>

                              <Box
                                sx={{
                                  width: "1px",
                                  backgroundColor: "#ccc",
                                  minHeight: "40px",
                                }}
                              />

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  borderTop: "1px solid #ccc",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  py: 0.5,
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                ${CommaSeparator(paidAmount)}
                              </Grid>
                            </Grid>

                            {/* BALANCE DUE */}
                            <Grid container>
                              <Grid
                                item
                                xs={7.8}
                                sx={{
                                  borderTop: "1px solid #ccc",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  py: 0.5,
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                Balance Due
                              </Grid>

                              <Box
                                sx={{
                                  width: "1px",
                                  backgroundColor: "#ccc",
                                  minHeight: "40px",
                                }}
                              />

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  borderTop: "1px solid #ccc",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  py: 0.5,
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                ${CommaSeparator(balanceAmount)}
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>

                    <Grid container spacing={2} justifyContent="space-between">
                      <Grid item xs={12} sm={6}>
                        <Grid
                          container
                          spacing={0.5}
                          sx={{ border: "1px solid #ddd", marginTop: "10px" }}
                        >
                          <Grid item xs={12}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontFamily: "Helvetica",
                                textAlign: "center",
                                fontWeight: "bold",
                                padding: "8px",
                                background: "#FFFCFC",
                              }}
                            >
                              Payment History
                            </Typography>
                          </Grid>
                          <Grid md={12} sx={{ paddingLeft: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{
                                  overflowX: "auto",
                                  maxWidth: "100%",
                                  paddingLeft: 0,
                                  marginLeft: 0,
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    margin: 0,
                                    padding: 0,
                                    tableLayout: "fixed",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        background: Colors.green,
                                        color: Colors.whiteSmoke,
                                        fontWeight: "bold",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          borderBottom: "2px solid #ddd",
                                          fontSize: "12px",
                                          textAlign: "center",
                                        }}
                                      >
                                        Pmt Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          borderBottom: "2px solid #ddd",
                                          fontSize: "12px",
                                          textAlign: "center",
                                        }}
                                      >
                                        Paid on
                                      </th>

                                      <th
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          borderBottom: "2px solid #ddd",
                                          fontSize: "12px",
                                          textAlign: "center",
                                        }}
                                      >
                                        Mode
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          borderBottom: "2px solid #ddd",
                                          fontSize: "12px",
                                          textAlign: "center",
                                        }}
                                      >
                                        USD
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          borderBottom: "2px solid #ddd",
                                          whiteSpace: "pre-line",
                                          fontSize: "12px",
                                          textAlign: "center",
                                        }}
                                      >
                                        AED
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {contanerDetail?.shipping?.vehicles?.map(
                                      (val, idx) =>
                                        val?.invoice?.payment?.map(
                                          (pay, pIdx) => (
                                            <>
                                              <tr
                                                style={{
                                                  background: "#FFFCFC",
                                                }}
                                                key={`${idx}-${pIdx}`}
                                              >
                                                <td
                                                  style={{
                                                    padding: "8px",
                                                    borderBottom:
                                                      "1px solid #ddd",
                                                    borderRight:
                                                      "1px solid #ddd",
                                                    fontSize: "12px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {pay?.shipping
                                                    ? "Shipping"
                                                    : "Vehicle"}
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "8px",
                                                    borderBottom:
                                                      "1px solid #ddd",
                                                    borderRight:
                                                      "1px solid #ddd",
                                                    fontSize: "12px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {moment(
                                                    pay?.payment_date
                                                  ).format("DD-MM-YYYY")}
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "8px",
                                                    borderBottom:
                                                      "1px solid #ddd",
                                                    borderRight:
                                                      "1px solid #ddd",
                                                    fontSize: "12px",
                                                    textAlign: "center",
                                                    textTransform: "capitalize",
                                                  }}
                                                >
                                                  {pay?.payment_medium || "-"}
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "8px",
                                                    borderBottom:
                                                      "1px solid #ddd",
                                                    borderRight:
                                                      "1px solid #ddd",
                                                    fontSize: "12px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  $
                                                  {CommaSeparator(
                                                    (pay?.currency?.toLowerCase() !==
                                                    "aed"
                                                      ? (Number(
                                                          pay?.shipping_amount
                                                        ) || 0) /
                                                        (Number(pay?.rate) || 1)
                                                      : Number(
                                                          pay?.shipping_amount
                                                        ) || 0
                                                    ).toFixed(2)
                                                  )}
                                                </td>
                                                <td
                                                  style={{
                                                    padding: "8px",
                                                    borderBottom:
                                                      "1px solid #ddd",
                                                    borderRight:
                                                      "1px solid #ddd",
                                                    fontSize: "12px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {CommaSeparator(
                                                    (pay?.currency?.toLowerCase() !==
                                                    "aed"
                                                      ? Number(
                                                          pay?.shipping_amount
                                                        ) || 0
                                                      : (Number(
                                                          pay?.shipping_amount
                                                        ) || 0) *
                                                        (Number(pay?.rate) || 0)
                                                    ).toFixed(2)
                                                  )}
                                                </td>
                                              </tr>
                                            </>
                                          )
                                        )
                                    )}
                                  </tbody>
                                </table>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        sx={{
                          textAlign: "end",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "end",
                        }}
                      >
                        <QRCodeCanvas
                          value={
                            window.location.origin +
                            `/consignment-container/${btoa(id)}`
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{ pl: 4, pr: 3, py: 1, bgcolor: Colors.primary, mt: 4 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: Colors.white,
                        fontFamily: FontFamily.openSans,
                      }}
                    >
                      Customer care Contact: Mohammed husni - +971523195682
                      (Arabic & English ) Ardamehr Shoev - +971545836028
                      (English ,Arabic, Tajik & Farsi)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: Colors.white,
                        fontFamily: FontFamily.openSans,
                      }}
                    >
                      Ravin abdul kareem - +971528293801 (Kurdish , Arabic &
                      English) Magsat Gylyjov - +97158666403 (Turken , Russian &
                      English)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: Colors.white,
                        fontFamily: FontFamily.openSans,
                      }}
                    >
                      Ind Area#4 P.O Box 83126, Sharjah, UAE.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </PDFExport>
        </>
      )}
    </Container>
  );
}

export default ConsignmentContainerInvoice;
